import { useState } from 'react'
import { useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
// import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import Button from '@mui/material/Button'
import GiteRoundedIcon from '@mui/icons-material/GiteRounded'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import { alpha } from '@mui/material/styles'

import ErrorBox from "@/components/error-box"
import LoadingBox from '@/components/loading-box'
import { useYearlyFeePayer } from '@/hooks/api/use-yearly-fee-payers'
import {
    COLLECTION_PERIOD_LABEL_MAP,
    HISTORY_RETURN_PARAM_KEY,
    PAYER_TYPE_LABEL_MAP,
    ROUTE_SEGMENTS,
} from '@/constants/general'
import { convertFromCents, printPrice, roundPrice } from '@/helpers'
import YearlyFeePayerForm from '@/components/forms/yearly-fee-payer'
import YearlyFeePayerDiscounts from './discounts'
import YearlyFeePayerFeePayments from './fee-payments'
import DetailRow from '@/components/detail-row'
import { selectedCollectionPointIdAtom } from '@/state'

function YearlyFeePayerDetail({
    payerId,
}: {
    payerId: number,
}) {

    const navigate = useNavigate()
        
    const setCollectionPointId = useSetAtom(selectedCollectionPointIdAtom)

    const [edit, setEdit] = useState<boolean>(false)
    
    const { data, status, error: queryError } = useYearlyFeePayer(payerId)

    if (status === 'pending') {
            return (
                <LoadingBox />
            )
        }

    if (queryError) {
        return <ErrorBox
            error={queryError}
            message={`Nepovedlo se načíst detail poplatníka ID ${payerId}`}
        />
    }

    const handleClickDetail = (id: number) => {
        setCollectionPointId(id)
        navigate(`/${ROUTE_SEGMENTS.base}/${ROUTE_SEGMENTS.collectionPoint}/${ROUTE_SEGMENTS.detail}?${HISTORY_RETURN_PARAM_KEY}=true`)
    }

    return (
        <>
            <Box px={2.5}>
                <Box sx={{
                    maxWidth: 'sm',
                }}>
                    {edit ?
                        <YearlyFeePayerForm
                            yearlyFeePayer={data}
                            closeCallback={() => setEdit(false)}
                        />
                        :
                        <Box sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: 'customGrey.boxBackgroundLight',
                            p: 2.5,
                            borderRadius: 2.5,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            flexGrow: 1,
                            overflow: 'hidden',
                        })}>
                            <Box
                                sx={(theme) => ({
                                position: 'absolute',
                                top: -30,
                                right: -30,
                                color: alpha(theme.palette.text.primary, 0.015),
                                zIndex: 0,
                                pointerEvents: 'none',
                                })}
                            >
                                <AccountCircleOutlinedIcon sx={{ fontSize: 200 }} />
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                flexGrow: 1,
                            }}>
                                <Typography variant="h4" component="h2" mb={2}>
                                    {data.name}
                                </Typography>
                                <DetailRow label="Variabilní symbol" wider>
                                    <Typography variant="body2" component="span">
                                        {data.variable_symbol}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Výše poplatku" wider>
                                    <Typography variant="body2" component="span">
                                        {printPrice(roundPrice(convertFromCents(data.requested_amount_cents)))}
                                    </Typography>
                                </DetailRow>
                                {/* <DetailRow label="Perioda výběru" wider>
                                    <Typography variant="body2" component="span">
                                        {COLLECTION_PERIOD_LABEL_MAP[data.collection_period]}
                                    </Typography>
                                </DetailRow> */}
                                <DetailRow label="Platí za" wider>
                                    <Typography variant="body2" component="span">
                                        {PAYER_TYPE_LABEL_MAP[data.payer_type]}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Svozové místo" wider>
                                    {data.household ?
                                        <Box sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1.5,
                                            alignItems: 'center',
                                        }}>
                                            <Tooltip title="Detail svozového místa">
                                                <Chip
                                                    label={`${data.household?.address?.street ?? '-'} ${data.household?.address?.street_number ?? '-'}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'customPrimaryLight.main'
                                                    }}
                                                    onClick={() => handleClickDetail(data.household.id)}
                                                    icon={<GiteRoundedIcon />}
                                                />
                                            </Tooltip>
                                            <Box sx={{ color: 'customGrey.secondaryText', fontSize: '14px' }}>
                                                {data.household.code}
                                            </Box>
                                        </Box>
                                        :
                                        '-'
                                    }
                                </DetailRow>
                                <Divider sx={{
                                    pt: 1,
                                    mb: 1,
                                }}/>
                                <DetailRow label="Email" wider>
                                    <Typography variant="body2" component="span" sx={{wordBreak: 'break-word'}}>
                                        {data.email_address ?? '-'}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Telefon" wider>
                                    <Typography variant="body2" component="span" sx={{wordBreak: 'break-word'}}>
                                        {data.phone_number ?? '-'}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Číslo bank. účtu" wider>
                                    <Typography variant="body2" component="span" sx={{wordBreak: 'break-word'}}>
                                        {data.account_number ?? '-'}
                                    </Typography>
                                </DetailRow>
                            </Box>
                            {/* <Box sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: '1rem',
                            }}>
                                <Tooltip title="Upravit">
                                    <IconButton
                                        id="edit-button"
                                        aria-label="edit"
                                        color="terciary"
                                        onClick={() => setEdit(true)}
                                        sx={{ backgroundColor: 'editableOrange.main' }}
                                    >
                                        <EditRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box> */}
                            <Box sx={{
                                px: 2.5,
                                pt: 2,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}>
                                <Button
                                    variant="outlined"
                                    color="terciary"
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => setEdit(true)}
                                    sx={{
                                        backgroundColor: 'customGrey.boxBackground',
                                    }}
                                >
                                    Upravit
                                </Button>
                            </Box>
                        </Box>
                    }
                </Box>
            </Box>
            <YearlyFeePayerDiscounts yearlyFeePayer={data} />
            <YearlyFeePayerFeePayments yearlyFeePayer={data} />
        </>
    )
}

export default YearlyFeePayerDetail
